'use client'

import React, { useState, useEffect, useMemo, useCallback } from 'react'
import { useSearchParams, useRouter } from 'next/navigation'
import { useRegionId } from '@/shared/hooks/use-region-id'
import { useRegion } from '@/shared/hooks/use-region'
import { useSession } from '@/features/auth/context/session.context'
import { UserRole } from '@/shared/constants/user-roles'
import {
  getWeeklyJobBoard,
  createWorkOrder,
  updateWorkOrder,
  WeeklyJobBoardResponse,
  JobBoardVolunteer,
  JobBoardTeam,
  createJobBoardTeam,
  updateJobBoardTeam,
  deleteJobBoardTeam,
} from '../../api/work-orders.api'
import { WeeklyJobBoardCalendar } from './WeeklyJobBoardCalendar'
import { JobBoardSideDrawer } from './JobBoardSideDrawer'
import { DailyVolunteerStaffingModal } from './DailyVolunteerStaffingModal'
import { WorkOrderApprovalModal } from './WorkOrderApprovalModal'
import { ManageTeamRosterModal } from './ManageTeamRosterModal'
import { JobBoardPrintView } from './JobBoardPrintView'
import { ExpandedPlanningView } from './ExpandedPlanningView'
import { Button } from '@/shared/components/ui/button'
import { Badge } from '@/shared/components/ui/badge'
import {
  ChevronLeft,
  ChevronRight,
  Printer,
  Users,
  Layers,
  UserCheck,
  RefreshCw,
  AlertCircle,
  CheckCircle,
  Loader2,
  SlidersHorizontal,
  Maximize2,
  Calendar as CalendarIcon,
} from 'lucide-react'
import { buildRegionUrl, cn } from '@/shared/lib/utils'
import { toast } from 'sonner'

// Helper: get Monday of the week containing a date
function getMonday(d: Date): Date {
  const date = new Date(d)
  const day = date.getDay()
  const diff = date.getDate() - day + (day === 0 ? -6 : 1) // adjust when day is sunday
  const monday = new Date(date.setDate(diff))
  monday.setHours(0, 0, 0, 0)
  return monday
}

function formatDateISO(d: Date): string {
  return d.toISOString().split('T')[0]
}

export function JobBoardContainer() {
  const { regionId, region, regionSlug, isLoading: isLoadingRegion } = useRegionId()
  const { session } = useSession()
  const searchParams = useSearchParams()
  const router = useRouter()

  // Week calculation
  const weekStartParam = searchParams.get('weekStart')
  const currentMonday = useMemo(() => {
    if (weekStartParam) {
      const parsed = new Date(weekStartParam + 'T00:00:00')
      if (!isNaN(parsed.getTime())) return parsed
    }
    return getMonday(new Date())
  }, [weekStartParam])

  const weekStartStr = useMemo(() => formatDateISO(currentMonday), [currentMonday])
  const weekEndStr = useMemo(() => {
    const sat = new Date(currentMonday)
    sat.setDate(sat.getDate() + 5) // Mon + 5 = Sat
    return formatDateISO(sat)
  }, [currentMonday])

  // 6 Work Days (Mon - Sat)
  const weekDates = useMemo(() => {
    const days: Array<{ date: string; dayName: string; dayNumber: string; isToday: boolean }> = []
    const todayStr = formatDateISO(new Date())
    const dayNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

    for (let i = 0; i < 6; i++) {
      const d = new Date(currentMonday)
      d.setDate(d.getDate() + i)
      const dStr = formatDateISO(d)
      days.push({
        date: dStr,
        dayName: dayNames[i],
        dayNumber: d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        isToday: dStr === todayStr,
      })
    }
    return days
  }, [currentMonday])

  // State
  const [boardData, setBoardData] = useState<WeeklyJobBoardResponse | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const [isExpandedPlanningMode, setIsExpandedPlanningMode] = useState(false)
  const [isPrintView, setIsPrintView] = useState(false)
  const [designatedLeaderIds, setDesignatedLeaderIds] = useState<number[]>([])

  const [selectedTeamForRoster, setSelectedTeamForRoster] = useState<
    | JobBoardTeam
    | {
        id?: number
        teamLeadId: number | null
        teamName: string
        volunteerIds: number[]
      }
    | null
  >(null)

  // Load designated leaders for this region and week
  useEffect(() => {
    if (!regionId || !weekStartStr) return
    try {
      const storageKey = `job_board_designated_leads_${regionId}_${weekStartStr}`
      const saved = localStorage.getItem(storageKey)
      if (saved) {
        setDesignatedLeaderIds(JSON.parse(saved))
      } else {
        setDesignatedLeaderIds([])
      }
    } catch {
      setDesignatedLeaderIds([])
    }
  }, [regionId, weekStartStr])

  const saveDesignatedLeaders = (ids: number[]) => {
    setDesignatedLeaderIds(ids)
    if (!regionId || !weekStartStr) return
    try {
      const storageKey = `job_board_designated_leads_${regionId}_${weekStartStr}`
      localStorage.setItem(storageKey, JSON.stringify(ids))
    } catch {}
  }

  const handleAddDesignatedLeader = async (userId: number) => {
    if (!regionId) return
    if (!designatedLeaderIds.includes(userId)) {
      const next = [...designatedLeaderIds, userId]
      saveDesignatedLeaders(next)
    }

    // Also persist team record to backend if not already existing
    const existingTeam = (boardData?.teams || []).find((t) => t.teamLeadId === userId)
    if (!existingTeam) {
      const leaderVol = (boardData?.weeklyVolunteers || []).find((v) => v.userId === userId)
      const leadName = leaderVol?.fullName || `Leader #${userId}`
      try {
        const res = await createJobBoardTeam({
          regionId,
          weekStart: weekStartStr,
          teamLeadId: userId,
          teamName: `${leadName}'s Team`,
          volunteerIds: [],
        })
        if (res.success) {
          toast.success('Team leader added to calendar')
          loadJobBoardData()
          return
        }
      } catch {}
    }
    toast.success('Team leader added to calendar')
  }

  const handleRemoveDesignatedLeader = async (userId: number) => {
    const next = designatedLeaderIds.filter((id) => id !== userId)
    saveDesignatedLeaders(next)

    // Also remove team record from backend if present
    const existingTeam = (boardData?.teams || []).find((t) => t.teamLeadId === userId)
    if (existingTeam) {
      try {
        await deleteJobBoardTeam(existingTeam.id)
        loadJobBoardData()
      } catch {}
    }
    toast.info('Leader row removed from board')
  }

  const handleAssignVolunteerToTeam = async (volunteerId: number, targetTeamId: number | null) => {
    if (!regionId || !boardData?.teams) return
    try {
      // If volunteer is currently in a team, remove them from that team
      for (const team of boardData.teams) {
        if (team.volunteerIds.includes(volunteerId) && team.id !== targetTeamId) {
          const updatedIds = team.volunteerIds.filter((id) => id !== volunteerId)
          await updateJobBoardTeam(team.id, { volunteerIds: updatedIds })
        }
      }

      // If assigning to a new team, add them
      if (targetTeamId) {
        const targetTeam = boardData.teams.find((t) => t.id === targetTeamId)
        if (targetTeam && !targetTeam.volunteerIds.includes(volunteerId)) {
          const updatedIds = [...targetTeam.volunteerIds, volunteerId]
          await updateJobBoardTeam(targetTeam.id, { volunteerIds: updatedIds })
        }
      }

      toast.success('Team roster updated')
      loadJobBoardData()
    } catch (err: any) {
      toast.error(err?.message || 'Failed to update team assignment')
    }
  }

  // Modals state
  const [selectedStaffing, setSelectedStaffing] = useState<{ workOrder: any; date: string } | null>(
    null
  )
  const [selectedApprovalWo, setSelectedApprovalWo] = useState<any | null>(null)

  // Role permissions
  const roleStr = String(session?.role || '').toLowerCase()
  const isPlanner =
    roleStr.includes('admin') ||
    roleStr.includes('base') ||
    roleStr.includes('team') ||
    session?.role === UserRole.ADMIN ||
    session?.role === UserRole.BASE_LEADER ||
    session?.role === UserRole.TEAM_LEADER
  const isTeamLead = roleStr.includes('team') || session?.role === UserRole.TEAM_LEADER

  // Fetch Board Data
  const loadJobBoardData = useCallback(async () => {
    if (!regionId) return
    setIsLoading(true)
    try {
      const res = await getWeeklyJobBoard(regionId, weekStartStr, weekEndStr)
      if (res.success && res.data) {
        setBoardData(res.data)
      } else {
        toast.error((res as any).message || 'Failed to load job board data')
      }
    } catch (err: any) {
      console.error('Error fetching job board:', err)
      toast.error(err?.message || 'Error loading job board')
    } finally {
      setIsLoading(false)
    }
  }, [regionId, weekStartStr, weekEndStr])

  useEffect(() => {
    loadJobBoardData()
  }, [loadJobBoardData])

  // Navigate to previous/next week
  const navigateWeek = (offsetWeeks: number) => {
    const nextMon = new Date(currentMonday)
    nextMon.setDate(nextMon.getDate() + offsetWeeks * 7)
    const nextStart = formatDateISO(nextMon)

    const params = new URLSearchParams(searchParams.toString())
    params.set('weekStart', nextStart)
    params.set('tab', 'job-board')

    const href = regionSlug
      ? buildRegionUrl(regionSlug, '/work-orders', Object.fromEntries(params.entries()))
      : `/work-orders?${params.toString()}`

    router.push(href)
  }

  // Handle scheduling a work scope from Priority Matrix
  const handleScheduleFromPriorityMatrix = async (scope: {
    homeId: number
    homeownerName: string
    address?: string
    workScopeId: number
    workScope: string
    workScopeDisplay: string
  }) => {
    try {
      // Default to starting on current week Monday
      const res = await createWorkOrder({
        homeId: scope.homeId,
        workOrderType: scope.workScopeDisplay || scope.workScope,
        workScopeId: scope.workScopeId,
        dateDelegated: weekStartStr,
        workOrderHours: [{ date: weekStartStr, hoursWorked: 0 }],
        volunteersNeeded: 4, // Default sensible crew size
        projectDescription: `Scheduled from Priority Matrix for ${scope.homeownerName}`,
      })

      if (res.success) {
        toast.success(`Work order created for ${scope.homeownerName}`)
        loadJobBoardData()
      } else {
        toast.error((res as any).message || 'Failed to create work order')
      }
    } catch (err: any) {
      toast.error(err?.message || 'Error creating work order')
    }
  }

  // Handle updating work order dates (resizing duration)
  const handleUpdateWorkOrderDates = async (workOrderId: number, newDates: string[]) => {
    try {
      const wo = (boardData?.workOrders || []).find((w) => w.id === workOrderId)
      const existingHours = Array.isArray(wo?.workOrderHours) ? wo.workOrderHours : []

      // Build updated workOrderHours maintaining existing volunteer assignments
      const updatedHours = newDates.map((d) => {
        const existing = existingHours.find((h: any) => h.date === d)
        return (
          existing || {
            date: d,
            hoursWorked: 0,
            volunteerIds: [],
          }
        )
      })

      const res = await updateWorkOrder(workOrderId, {
        workOrderHours: updatedHours,
        dateDelegated: newDates[0],
      })

      if (res.success) {
        loadJobBoardData()
      } else {
        toast.error((res as any).message || 'Failed to update schedule dates')
      }
    } catch (err: any) {
      toast.error(err?.message || 'Error updating schedule')
    }
  }

  // Handle moving / dragging work order to new dates and/or new team
  const handleMoveWorkOrder = async (
    workOrderId: number,
    targetTeam: JobBoardTeam | null,
    newDates: string[]
  ) => {
    try {
      const wo = (boardData?.workOrders || []).find((w) => w.id === workOrderId)
      const existingHours = Array.isArray(wo?.workOrderHours) ? wo.workOrderHours : []

      // Build updated workOrderHours maintaining existing volunteer assignments when date matches
      const updatedHours = newDates.map((d) => {
        const existing = existingHours.find((h: any) => h.date === d)
        return (
          existing || {
            date: d,
            hoursWorked: 0,
            volunteerIds: targetTeam?.volunteerIds || [],
          }
        )
      })

      const payload: any = {
        workOrderHours: updatedHours,
        dateDelegated: newDates[0],
        teamName: targetTeam ? targetTeam.teamName : null,
        teamLeadId: targetTeam ? (targetTeam.teamLeadId ?? 0) : 0,
      }

      const res = await updateWorkOrder(workOrderId, payload)

      if (res.success) {
        loadJobBoardData()
      } else {
        toast.error((res as any).message || 'Failed to move work order')
      }
    } catch (err: any) {
      toast.error(err?.message || 'Error moving work order')
    }
  }

  // Handle dropping a new work scope from backlog directly onto a team / day cell
  const handleDropNewWorkScope = async (
    scopeItem: any,
    targetTeam: JobBoardTeam | null,
    date: string
  ) => {
    try {
      const res = await createWorkOrder({
        homeId: scopeItem.homeId,
        workScopeId: scopeItem.workScopeId,
        workOrderType: scopeItem.workScopeDisplay || scopeItem.workScope,
        teamName: targetTeam ? targetTeam.teamName : undefined,
        teamLeadId: targetTeam?.teamLeadId ? targetTeam.teamLeadId : undefined,
        dateDelegated: date,
        volunteersNeeded: 3,
        workOrderHours: [
          {
            date,
            hoursWorked: 0,
            volunteerIds: targetTeam?.volunteerIds || [],
          },
        ],
      })
      if (res.success) {
        toast.success(
          `Scheduled ${scopeItem.workScopeDisplay || 'work order'} for ${date}`
        )
        loadJobBoardData()
      } else {
        toast.error((res as any).message || 'Failed to schedule work order')
      }
    } catch (err: any) {
      toast.error(err?.message || 'Error scheduling work order')
    }
  }

  // Handle assigning team leader to a team
  const handleAssignLeaderToTeam = async (teamId: number, leaderId: number) => {
    try {
      const res = await updateJobBoardTeam(teamId, { teamLeadId: leaderId })
      if (res.success) {
        toast.success('Team leader assigned to team')
        loadJobBoardData()
      } else {
        toast.error(res.message || 'Failed to assign leader')
      }
    } catch (err: any) {
      toast.error(err?.message || 'Error assigning leader')
    }
  }

  // Handle deleting an empty team
  const handleDeleteTeam = async (teamId: number) => {
    try {
      const res = await deleteJobBoardTeam(teamId)
      if (res.success) {
        toast.info('Team removed')
        loadJobBoardData()
      } else {
        toast.error(res.message || 'Failed to remove team')
      }
    } catch (err: any) {
      toast.error(err?.message || 'Error removing team')
    }
  }

  // Handle assigning team leader to work order directly
  const handleAssignTeamLead = async (workOrderId: number, teamLeadId: number | null) => {
    try {
      const res = await updateWorkOrder(workOrderId, {
        teamLeadId: teamLeadId || 0,
      })
      if (res.success) {
        toast.success('Team leader assigned')
        loadJobBoardData()
      } else {
        toast.error((res as any).message || 'Error assigning team leader')
      }
    } catch (err: any) {
      toast.error(err?.message || 'Error assigning team leader')
    }
  }

  if (isPrintView && boardData) {
    return (
      <JobBoardPrintView
        weekStart={weekStartStr}
        weekEnd={weekEndStr}
        regionName={region?.name}
        totalVolunteersOnSite={boardData.totalVolunteersOnSite}
        totalVolunteersNeeded={boardData.totalVolunteersNeeded}
        weekDates={weekDates}
        workOrders={boardData.workOrders || []}
        onBack={() => setIsPrintView(false)}
      />
    )
  }

  const volunteersOnSite = boardData?.totalVolunteersOnSite || 0
  const headcountNeeded = boardData?.totalVolunteersNeeded || 0
  const capacitySurplus = volunteersOnSite - headcountNeeded

  return (
    <div className="flex flex-col space-y-4">
      {/* Top Header Controls: Week Navigation & Live Capacity Bar */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3 bg-muted/30 p-3.5 rounded-xl border">
        {/* Week Switcher */}
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="icon"
            onClick={() => navigateWeek(-1)}
            className="h-8 w-8"
            title="Previous Week"
          >
            <ChevronLeft className="w-4 h-4" />
          </Button>

          <div className="text-sm font-semibold px-2 min-w-[200px] text-center">
            Week of {weekDates[0]?.dayNumber} – {weekDates[5]?.dayNumber}
          </div>

          <Button
            variant="outline"
            size="icon"
            onClick={() => navigateWeek(1)}
            className="h-8 w-8"
            title="Next Week"
          >
            <ChevronRight className="w-4 h-4" />
          </Button>

          <Button
            variant="ghost"
            size="sm"
            onClick={loadJobBoardData}
            className="h-8 text-xs text-muted-foreground hover:text-foreground gap-1"
          >
            <RefreshCw className={cn('w-3.5 h-3.5', isLoading && 'animate-spin')} />
            Refresh
          </Button>
        </div>

        {/* Capacity Balancing Bar (Planner view) */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2 bg-background px-3 py-1.5 rounded-lg border text-xs shadow-sm">
            <Users className="w-4 h-4 text-primary" />
            <span>
              <strong>{volunteersOnSite}</strong> Volunteers on Site
            </span>
            <span className="text-muted-foreground">|</span>
            <span>
              <strong>{headcountNeeded}</strong> Headcount Needed
            </span>
            <Badge
              variant={capacitySurplus >= 0 ? 'secondary' : 'destructive'}
              className="text-[10px] ml-1"
            >
              {capacitySurplus >= 0 ? (
                <span className="flex items-center gap-1 text-emerald-700 dark:text-emerald-400">
                  <CheckCircle className="w-3 h-3" /> +{capacitySurplus} Available
                </span>
              ) : (
                <span className="flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" /> {capacitySurplus} Deficit
                </span>
              )}
            </Badge>
          </div>

          {/* Action Buttons: View Mode Switcher, Planning Drawer, and Print */}
          <div className="flex items-center gap-2">
            {isPlanner && (
              <div className="flex items-center rounded-lg border bg-background p-0.5 shadow-2xs">
                <Button
                  variant={!isExpandedPlanningMode ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setIsExpandedPlanningMode(false)}
                  className="h-7 text-xs px-2.5 gap-1.5 font-medium"
                  title="6-Day Calendar View"
                >
                  <CalendarIcon className="w-3.5 h-3.5" />
                  Calendar
                </Button>
                <Button
                  variant={isExpandedPlanningMode ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => {
                    setIsExpandedPlanningMode(true)
                    setIsDrawerOpen(false)
                  }}
                  className="h-7 text-xs px-2.5 gap-1.5 font-medium"
                  title="Initial Planning Mode (Full Working Area)"
                >
                  <Maximize2 className="w-3.5 h-3.5" />
                  Initial Planning
                </Button>
              </div>
            )}

            {isPlanner && !isExpandedPlanningMode && (
              <Button
                variant={isDrawerOpen ? 'default' : 'outline'}
                size="sm"
                onClick={() => setIsDrawerOpen(!isDrawerOpen)}
                className="h-8 text-xs gap-1.5 font-medium shadow-xs"
                title={isDrawerOpen ? 'Close Planning Drawer' : 'Open Planning Drawer'}
              >
                <SlidersHorizontal className="w-3.5 h-3.5" />
                Planning Drawer
              </Button>
            )}

            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsPrintView(true)}
              className="h-8 text-xs gap-1.5"
              title="Printable Job Board"
            >
              <Printer className="w-3.5 h-3.5" />
              Print
            </Button>
          </div>
        </div>
      </div>

      {/* Main Board Layout: Expanded Planning Mode vs Calendar View */}
      {isExpandedPlanningMode && isPlanner ? (
        <ExpandedPlanningView
          weekStart={weekStartStr}
          weekEnd={weekEndStr}
          weekDates={weekDates}
          regionId={regionId || undefined}
          volunteers={boardData?.weeklyVolunteers || []}
          candidateTeamLeads={boardData?.candidateTeamLeads || []}
          groups={boardData?.groups || []}
          teams={boardData?.teams || []}
          activeLeaderIds={[
            ...designatedLeaderIds,
            ...(boardData?.teams || [])
              .map((t) => t.teamLeadId)
              .filter((id): id is number => typeof id === 'number'),
            ...(boardData?.workOrders || [])
              .map((w) => w.teamLead?.id)
              .filter((id): id is number => typeof id === 'number'),
          ]}
          onScheduleWorkScope={handleScheduleFromPriorityMatrix}
          onAssignToTeam={handleAssignVolunteerToTeam}
          onToggleLeader={(vol) => {
            if (designatedLeaderIds.includes(vol.userId)) {
              handleRemoveDesignatedLeader(vol.userId)
            } else {
              handleAddDesignatedLeader(vol.userId)
            }
          }}
          onCloseExpandedMode={() => setIsExpandedPlanningMode(false)}
          onRefresh={loadJobBoardData}
          isLoading={isLoading}
        />
      ) : (
        <div className="flex gap-4 items-start relative min-h-[550px]">
          {/* Main Calendar View */}
          <div className="flex-1 w-full overflow-x-auto min-w-0">
            {isLoading && !boardData ? (
              <div className="flex items-center justify-center py-24 gap-2 text-sm text-muted-foreground">
                <Loader2 className="w-5 h-5 animate-spin" />
                Loading digital job board...
              </div>
            ) : (
              <WeeklyJobBoardCalendar
                weekDates={weekDates}
                workOrders={boardData?.workOrders || []}
                candidateTeamLeads={boardData?.candidateTeamLeads || []}
                allVolunteers={boardData?.weeklyVolunteers || []}
                designatedLeaderIds={designatedLeaderIds}
                teams={boardData?.teams || []}
                isPlanner={isPlanner}
                isTeamLead={isTeamLead}
                currentUserId={session?.sub}
                onOpenStaffingModal={(wo, date) => setSelectedStaffing({ workOrder: wo, date })}
                onOpenApprovalModal={(wo) => setSelectedApprovalWo(wo)}
                onOpenManageRoster={(team) => setSelectedTeamForRoster(team)}
                onUpdateWorkOrderDates={handleUpdateWorkOrderDates}
                onAssignTeamLead={handleAssignTeamLead}
                onMoveWorkOrder={handleMoveWorkOrder}
                onDropNewWorkScope={handleDropNewWorkScope}
                onAssignLeaderToTeam={handleAssignLeaderToTeam}
                onDeleteTeam={handleDeleteTeam}
                onAddLeaderRow={handleAddDesignatedLeader}
                onRemoveLeaderRow={handleRemoveDesignatedLeader}
              />
            )}
          </div>

          {/* Unified Tabbed Planning Drawer (Priority Matrix + Volunteers) */}
          {isPlanner && (
            <JobBoardSideDrawer
              isOpen={isDrawerOpen}
              onToggle={() => setIsDrawerOpen(!isDrawerOpen)}
              onExpandMode={() => {
                setIsExpandedPlanningMode(true)
                setIsDrawerOpen(false)
              }}
              regionId={regionId || undefined}
              volunteers={boardData?.weeklyVolunteers || []}
              candidateTeamLeads={boardData?.candidateTeamLeads || []}
              teams={boardData?.teams || []}
              activeLeaderIds={[
                ...designatedLeaderIds,
                ...(boardData?.teams || [])
                  .map((t) => t.teamLeadId)
                  .filter((id): id is number => typeof id === 'number'),
                ...(boardData?.workOrders || [])
                  .map((w) => w.teamLead?.id)
                  .filter((id): id is number => typeof id === 'number'),
              ]}
              onToggleLeader={(vol) => {
                if (designatedLeaderIds.includes(vol.userId)) {
                  handleRemoveDesignatedLeader(vol.userId)
                } else {
                  handleAddDesignatedLeader(vol.userId)
                }
              }}
              onAssignToTeam={handleAssignVolunteerToTeam}
              onScheduleWorkScope={handleScheduleFromPriorityMatrix}
            />
          )}
        </div>
      )}

      {/* Manage Team Roster Modal */}
      {selectedTeamForRoster && regionId && (
        <ManageTeamRosterModal
          isOpen={true}
          onClose={() => setSelectedTeamForRoster(null)}
          team={selectedTeamForRoster}
          weekStart={weekStartStr}
          regionId={regionId}
          allVolunteers={boardData?.weeklyVolunteers || []}
          onSaved={loadJobBoardData}
        />
      )}

      {/* Daily Volunteer Staffing Modal */}
      {selectedStaffing && regionId && (
        <DailyVolunteerStaffingModal
          isOpen={true}
          onClose={() => setSelectedStaffing(null)}
          workOrder={selectedStaffing.workOrder}
          date={selectedStaffing.date}
          regionId={regionId}
          onSaved={loadJobBoardData}
        />
      )}

      {/* Approval & Review Modal */}
      {selectedApprovalWo && (
        <WorkOrderApprovalModal
          isOpen={true}
          onClose={() => setSelectedApprovalWo(null)}
          workOrder={selectedApprovalWo}
          onSaved={loadJobBoardData}
        />
      )}
    </div>
  )
}
